import { PendoScreenInventory } from '@pendo/components-vue3';
import { MAX_LABELS, MAX_READ_ROWS } from './screen-inventory.constants';
import type { InventoryCandidate, PageDataResult } from './screen-inventory.types';
import { isText } from './screen-inventory.utils';

interface TableDescription {
    title: string;
    columns: Array<{ label: string }>;
    rowCount: number;
    sort: { label: string; direction: 'ascending' | 'descending' } | null;
    loading: boolean;
}

interface TableRead {
    columns: string[];
    rows: string[][];
    loadedRowCount: number;
    totalRowCount: number;
}

interface TableEntry {
    element: Element;
    describe: () => TableDescription;
    read?: (options: { maxRows: number }) => TableRead;
}

function count (amount: number, noun: string): string {
    return `${amount} ${amount === 1 ? noun : `${noun}s`}`;
}

function readTable (read: NonNullable<TableEntry['read']>, loading: boolean): PageDataResult {
    if (loading) return { error: { code: 'still_loading', message: 'This table is still loading. Try again in a moment.' } };

    const { columns, rows, loadedRowCount, totalRowCount } = read({ maxRows: MAX_READ_ROWS });
    const notes = [
        ...(rows.length < loadedRowCount ? [`Only the first ${rows.length} of ${loadedRowCount} rows are included, in the order shown on screen.`] : []),
        ...(totalRowCount > loadedRowCount ? [`The table has ${totalRowCount} rows, but only the ${loadedRowCount} loaded in the browser are included.`] : []),
    ];

    return { columns, rows, rowCount: totalRowCount, ...(notes.length && { notes }) };
}

function toCandidate ({ element, describe, read }: TableEntry): InventoryCandidate {
    const { title, columns, rowCount, sort, loading } = describe();
    const labels = columns.map((column) => column.label).filter(isText);
    const shape = [count(rowCount, 'row'), count(labels.length, 'column'), ...(loading ? ['loading'] : [])];

    return {
        element,
        kind: 'table',
        ...(isText(title) && { title }),
        ...(labels.length && { fallbackName: labels[0] }),
        shape: shape.join(', '),
        ...(sort && isText(sort.label) && { details: [{ name: `sorted ${sort.direction} by`, text: sort.label }] }),
        labelsOf: 'columns',
        labels: labels.slice(0, MAX_LABELS).map((text) => ({ text })),
        labelTotal: labels.length,
        ...(read && { read: () => readTable(read, loading) }),
    };
}

export function collectTableCandidates (): InventoryCandidate[] {
    const entries: TableEntry[] = PendoScreenInventory.getEntries();

    return entries.map(toCandidate);
}
